import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point as turfPoint, polygon as turfPolygon } from "@turf/helpers";

/**
 * Point-in-polygon check using Turf.js
 * Uses the same logic as the attendance map component
 * @param point - The point to check {lat: number, lng: number}
 * @param polygon - Array of polygon vertices [{lat: number, lng: number}, ...]
 * @returns true if point is inside polygon, false otherwise
 */
export const isPointInPolygon = (
  point: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>
): boolean => {
  if (!polygon || polygon.length < 3) {
    return false;
  }

  try {
    // Convert to Turf.js format: [lng, lat] for GeoJSON (same as attendance map example)
    // Example: turfPoint([avgPos.lng, avgPos.lat])
    const pt = turfPoint([point.lng, point.lat]);

    // Convert polygon coordinates: [[lng, lat], [lng, lat], ...]
    // Example: officePolygonCoords.map(c => [c.lng, c.lat])
    const polyCoords = polygon.map((c) => [c.lng, c.lat]);

    // Close the polygon (first point = last point) - required by GeoJSON
    // Turf.js polygon requires closed ring
    if (polyCoords.length > 0) {
      const firstCoord = polyCoords[0];
      const lastCoord = polyCoords[polyCoords.length - 1];
      // Check if polygon is already closed
      if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
        polyCoords.push([firstCoord[0], firstCoord[1]]);
      }
    }

    // Create polygon: turfPolygon([coords]) - same as example
    const poly = turfPolygon([polyCoords]);
    return booleanPointInPolygon(pt, poly);
  } catch (e) {
    console.error("Error checking point in polygon with Turf.js:", e);
    // Fallback: Ray casting algorithm
    return isPointInPolygonFallback(point, polygon);
  }
};

/**
 * Fallback: Ray casting algorithm for point-in-polygon check
 * Used if Turf.js fails
 */
const isPointInPolygonFallback = (
  point: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>
): boolean => {
  const { lat, lng } = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const intersect =
      (yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Office polygon coordinates (static data - DEPRECATED, use API data instead)
 * This is kept only as a last resort fallback and should not be used in production
 */
export const OFFICE_POLYGON = [
  { lat: 28.605010162012306, lng: 77.28925405749274 },
  { lat: 28.604934807695063, lng: 77.2890314341445 },
  { lat: 28.603248740714136, lng: 77.28988169440223 },
  { lat: 28.603347644831516, lng: 77.2901123643775 },
];

/**
 * Calculate distance between two points using Haversine formula (in meters)
 * @param point1 - First point {lat: number, lng: number}
 * @param point2 - Second point {lat: number, lng: number}
 * @returns Distance in meters
 */
export const calculateDistance = (
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number => {
  const R = 6371000; // Earth's radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Calculate center point of polygon
 * @param polygon - Array of polygon vertices
 * @returns Center point {lat: number, lng: number}
 */
export const getPolygonCenter = (
  polygon: Array<{ lat: number; lng: number }>
): { lat: number; lng: number } => {
  let sumLat = 0;
  let sumLng = 0;
  polygon.forEach((point) => {
    sumLat += point.lat;
    sumLng += point.lng;
  });
  return {
    lat: sumLat / polygon.length,
    lng: sumLng / polygon.length,
  };
};

/**
 * Find nearest point on polygon boundary to given point
 * @param point - The point to check
 * @param polygon - Array of polygon vertices
 * @returns Nearest point and distance
 */
export const findNearestBoundaryPoint = (
  point: { lat: number; lng: number },
  polygon: Array<{ lat: number; lng: number }>
): { point: { lat: number; lng: number }; distance: number } => {
  let minDistance = Infinity;
  let nearestPoint = polygon[0];

  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const segmentStart = polygon[i];
    const segmentEnd = polygon[j];

    // Calculate distance to line segment
    const distance = calculateDistanceToSegment(
      point,
      segmentStart,
      segmentEnd
    );
    if (distance < minDistance) {
      minDistance = distance;
      // Find closest point on segment
      const t = Math.max(
        0,
        Math.min(1, getProjectionT(point, segmentStart, segmentEnd))
      );
      nearestPoint = {
        lat: segmentStart.lat + t * (segmentEnd.lat - segmentStart.lat),
        lng: segmentStart.lng + t * (segmentEnd.lng - segmentStart.lng),
      };
    }
  }

  return { point: nearestPoint, distance: minDistance };
};

/**
 * Calculate distance from point to line segment
 */
const calculateDistanceToSegment = (
  point: { lat: number; lng: number },
  segmentStart: { lat: number; lng: number },
  segmentEnd: { lat: number; lng: number }
): number => {
  const t = getProjectionT(point, segmentStart, segmentEnd);
  const clampedT = Math.max(0, Math.min(1, t));
  const closestPoint = {
    lat: segmentStart.lat + clampedT * (segmentEnd.lat - segmentStart.lat),
    lng: segmentStart.lng + clampedT * (segmentEnd.lng - segmentStart.lng),
  };
  return calculateDistance(point, closestPoint);
};

/**
 * Get projection parameter t for point on line segment
 */
const getProjectionT = (
  point: { lat: number; lng: number },
  segmentStart: { lat: number; lng: number },
  segmentEnd: { lat: number; lng: number }
): number => {
  const dx = segmentEnd.lng - segmentStart.lng;
  const dy = segmentEnd.lat - segmentStart.lat;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return 0;
  }

  const t =
    ((point.lng - segmentStart.lng) * dx +
      (point.lat - segmentStart.lat) * dy) /
    lengthSquared;
  return t;
};

/**
 * Check if check-in location is inside office polygon
 * @param latitude - Check-in latitude
 * @param longitude - Check-in longitude
 * @param polygon - REQUIRED polygon coordinates from API (no static fallback)
 * @returns true if inside office, false otherwise
 */
export const isCheckInLocationInOffice = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  polygon: Array<{ lat: number; lng: number }> | null | undefined
): boolean => {
  if (!latitude || !longitude) {
    return false;
  }

  // Require polygon from API - no static fallback
  if (!polygon || !Array.isArray(polygon) || polygon.length < 3) {
    console.warn("⚠️ No valid polygon provided - cannot check location");
    return false;
  }

  return isPointInPolygon({ lat: latitude, lng: longitude }, polygon);
};

/**
 * Get detailed location information
 * @param latitude - Check-in latitude
 * @param longitude - Check-in longitude
 * @param polygon - REQUIRED polygon coordinates from API (no static fallback)
 * @returns Detailed location information
 */
export const getLocationDetails = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  polygon: Array<{ lat: number; lng: number }> | null | undefined
): {
  isInOffice: boolean;
  distanceFromCenter: number;
  distanceFromNearestBoundary: number;
  officeCenter: { lat: number; lng: number };
} | null => {
  if (!latitude || !longitude) {
    return null;
  }

  // Require polygon from API - no static fallback
  if (!polygon || !Array.isArray(polygon) || polygon.length < 3) {
    console.warn(
      "⚠️ No valid polygon provided - cannot calculate location details"
    );
    return null;
  }

  const checkInPoint = { lat: latitude, lng: longitude };
  const isInOffice = isPointInPolygon(checkInPoint, polygon);
  const officeCenter = getPolygonCenter(polygon);
  const distanceFromCenter = calculateDistance(checkInPoint, officeCenter);
  const nearestBoundary = findNearestBoundaryPoint(checkInPoint, polygon);

  return {
    isInOffice,
    distanceFromCenter,
    distanceFromNearestBoundary: nearestBoundary.distance,
    officeCenter,
  };
};
